import React from 'react';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../../../../core/config/api';

type PlayerRow = {
  id: number;
  username: string;
  email: string;
  roles_id: number;
  activated: number;
  premium?: number;
  argents?: number;
  medailles?: number;
};

/**
 * Liste joueurs orientée premium / économie (admin + CM).
 * La fiche complète reste sur /panel/moderation/manage/:id.
 */
const PanelPlayersPremiumV1: React.FC = () => {
  const [rows, setRows] = React.useState<PlayerRow[]>([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const q = search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/users-roles/users?page=1&limit=50${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement');
      const list = Array.isArray(data.data) ? data.data : [];
      const withProfiles = await Promise.all(
        list.map(async (u: PlayerRow) => {
          try {
            const pr = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/player/${u.id}/profile`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const pd = await pr.json();
            if (pr.ok && pd?.success && pd?.data) {
              return {
                ...u,
                premium: Number(pd.data.premium || 0),
                argents: Number(pd.data.argents || 0),
                medailles: Number(pd.data.medailles || 0),
              };
            }
          } catch {
            // ignore per row
          }
          return u;
        }),
      );
      setRows(withProfiles.filter((u) => u.roles_id === 6));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [search]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        Outil <strong className="text-slate-200">staff uniquement</strong> : gérer le statut premium et l’économie des
        comptes <strong className="text-slate-200">joueurs</strong> (rôle 6). Cela n’accorde{' '}
        <strong className="text-amber-200/90">pas</strong> l’accès au panel — réservé à l’équipe (rôles 1 à 4).
      </p>

      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void load()}
          placeholder="Rechercher un joueur…"
          className="min-w-[200px] flex-1 rounded-lg border border-[#243052]/90 bg-[#0d152b]/75 px-3 py-2 text-sm text-white backdrop-blur-sm"
        />
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-sky-500/40 bg-sky-500/15 px-4 py-2 text-sm font-medium text-sky-100 hover:bg-sky-500/25"
        >
          Rechercher
        </button>
      </div>

      {loading && <p className="text-slate-300">Chargement…</p>}
      {error && <p className="text-red-300">{error}</p>}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-xl border border-[#243052]/90 bg-[#0d152b]/75 backdrop-blur-sm">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-[#243052] text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Joueur</th>
                <th className="px-4 py-3">Premium</th>
                <th className="px-4 py-3">Argents</th>
                <th className="px-4 py-3">Médailles</th>
                <th className="px-4 py-3">Compte</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Aucun joueur trouvé.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b border-[#1e2d4d]/80 text-slate-200 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{row.username}</div>
                      <div className="text-xs text-slate-500">{row.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {Number(row.premium) ? (
                        <span className="rounded bg-amber-500/20 px-2 py-0.5 text-xs text-amber-200">Premium</span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{Number(row.argents ?? 0).toLocaleString('fr-FR')}</td>
                    <td className="px-4 py-3">{Number(row.medailles ?? 0).toLocaleString('fr-FR')}</td>
                    <td className="px-4 py-3">
                      {row.activated ? (
                        <span className="text-emerald-400">Actif</span>
                      ) : (
                        <span className="text-red-400">Inactif</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/panel/moderation/manage/${row.id}`}
                        className="inline-flex rounded-lg border border-violet-500/40 bg-violet-500/15 px-3 py-1.5 text-xs font-medium text-violet-100 hover:bg-violet-500/25"
                      >
                        Gestion complète
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PanelPlayersPremiumV1;
